import type { QueryClient } from '@tanstack/react-query';
import { ordersApi } from '../api';
import { ordersKeys } from '../hooks/useOrders';
import type { OrderResponse, OrderStatus } from '../types';

/**
 * Apply an owner-notification event to react-query caches. Lives in orders
 * (not notifications) so the notifications feature doesn't need to know
 * about OrderStatus, cache keys, or the order fetch-by-id client. Callers
 * in notifications pass in just the event payload and the queryClient.
 *
 * Behavior:
 *  - ORDER_PLACED → fetch the new order and prepend to all-orders + pending caches
 *  - ORDER_ACCEPTED → patch status to CONFIRMED, invalidate detail so
 *    server-authoritative fields (autoAcceptedBySystem, timestamps) populate
 *  - ORDER_REJECTED → patch status to CANCELLED, invalidate detail
 *  - anything else → no-op (forward-compat)
 */
export function applyOrderEventToCaches(
  queryClient: QueryClient,
  restaurantId: string,
  event: { eventType: string; orderId: string }
): void {
  switch (event.eventType) {
    case 'ORDER_PLACED':
      enrichNewOrderCaches(queryClient, restaurantId, event.orderId);
      break;
    case 'ORDER_ACCEPTED':
      patchOrderCache(queryClient, restaurantId, event.orderId, 'CONFIRMED');
      queryClient.invalidateQueries({ queryKey: ['order', restaurantId, event.orderId] });
      // Remove from scheduled cache — covers the owner start-preparing path.
      queryClient.setQueryData<OrderResponse[] | undefined>(
        ordersKeys.scheduled(restaurantId),
        (prev) => (Array.isArray(prev) ? prev.filter((o) => o.orderId !== event.orderId) : prev)
      );
      break;
    case 'ORDER_REJECTED':
      patchOrderCache(queryClient, restaurantId, event.orderId, 'CANCELLED');
      queryClient.invalidateQueries({ queryKey: ['order', restaurantId, event.orderId] });
      break;
    case 'SCHEDULED_ORDER_PLACED':
      enrichScheduledOrderCaches(queryClient, restaurantId, event.orderId);
      break;
    case 'SCHEDULED_ORDER_RELEASED':
      // Dead event — backend no longer emits this. Kept for backward-compat.
      queryClient.invalidateQueries({ queryKey: ordersKeys.scheduled(restaurantId) });
      queryClient.invalidateQueries({ queryKey: ordersKeys.active(restaurantId) });
      queryClient.invalidateQueries({ queryKey: ['order', restaurantId, event.orderId] });
      break;
    default:
      break;
  }
}

function enrichScheduledOrderCaches(
  queryClient: QueryClient,
  restaurantId: string,
  orderId: string
): void {
  ordersApi
    .getOrder(restaurantId, orderId)
    .then((order) => {
      queryClient.setQueryData(ordersKeys.detail(restaurantId, orderId), order);
      queryClient.setQueryData<OrderResponse[] | undefined>(
        ordersKeys.scheduled(restaurantId),
        (prev) => prependUnique(prev, order)
      );
    })
    .catch(() => {
      queryClient.invalidateQueries({ queryKey: ordersKeys.scheduled(restaurantId) });
    });
}

function enrichNewOrderCaches(
  queryClient: QueryClient,
  restaurantId: string,
  orderId: string
): void {
  ordersApi
    .getOrder(restaurantId, orderId)
    .then((order) => {
      queryClient.setQueryData(ordersKeys.detail(restaurantId, orderId), order);
      // Date-range list caches each have their own entry; fan the new order
      // into every one so it surfaces regardless of which window is active.
      queryClient.setQueriesData<OrderResponse[]>(
        { queryKey: ordersKeys.listPrefix(restaurantId) },
        (prev) => prependUnique(prev, order)
      );
      queryClient.setQueryData<OrderResponse[] | undefined>(
        ordersKeys.byStatus(restaurantId, 'PENDING'),
        (prev) => prependUnique(prev, order)
      );
    })
    .catch(() => {
      queryClient.invalidateQueries({
        queryKey: ordersKeys.byStatus(restaurantId, 'PENDING'),
      });
    });
}

function prependUnique(
  prev: OrderResponse[] | undefined,
  order: OrderResponse
): OrderResponse[] | undefined {
  if (!Array.isArray(prev)) return prev;
  if (prev.some((o) => o.orderId === order.orderId)) return prev;
  return [order, ...prev];
}

function patchOrderCache(
  queryClient: QueryClient,
  restaurantId: string,
  orderId: string,
  newStatus: OrderStatus
): void {
  const nowIso = new Date().toISOString();

  queryClient.setQueryData<OrderResponse | undefined>(
    ordersKeys.detail(restaurantId, orderId),
    (prev) => (prev ? applyStatusPatch(prev, newStatus, nowIso) : prev)
  );

  // Fan out across every cached list variant — distinct date-range selections
  // each keep their own cache entry, and the patched order has to land in all
  // of them or it disappears from view (e.g. server-side auto-accept moving
  // PENDING → CONFIRMED).
  queryClient.setQueriesData<OrderResponse[]>(
    { queryKey: ordersKeys.listPrefix(restaurantId) },
    (prev) =>
      prev
        ? prev.map((o) => (o.orderId === orderId ? applyStatusPatch(o, newStatus, nowIso) : o))
        : prev
  );

  if (newStatus !== 'PENDING') {
    queryClient.setQueryData<OrderResponse[] | undefined>(
      ordersKeys.byStatus(restaurantId, 'PENDING'),
      (prev) => (Array.isArray(prev) ? prev.filter((o) => o.orderId !== orderId) : prev)
    );
  }

  // The active set is keyed independently of date and aggregates several
  // statuses — refetch is simpler than per-status cache surgery.
  queryClient.invalidateQueries({ queryKey: ordersKeys.active(restaurantId) });
}

function applyStatusPatch(
  order: OrderResponse,
  newStatus: OrderStatus,
  nowIso: string
): OrderResponse {
  const next: OrderResponse = { ...order, orderStatus: newStatus };
  switch (newStatus) {
    case 'CONFIRMED':
      next.confirmedAt = next.confirmedAt ?? nowIso;
      next.autoAcceptAt = null;
      break;
    case 'PREPARING':
      next.preparingAt = next.preparingAt ?? nowIso;
      break;
    case 'IN_DELIVERY':
      next.inDeliveryAt = next.inDeliveryAt ?? nowIso;
      break;
    case 'READY_FOR_PICKUP':
      next.readyForPickupAt = next.readyForPickupAt ?? nowIso;
      break;
    case 'COMPLETED':
      next.completedAt = next.completedAt ?? nowIso;
      break;
    case 'CANCELLED':
      next.cancelledAt = next.cancelledAt ?? nowIso;
      next.autoAcceptAt = null;
      break;
    default:
      break;
  }
  return next;
}
